/* eslint-disable max-lines */
import { addLoader, removeLoader } from '../stores/loading.js';
import { addMessage } from '../stores/messages.js';
import { ENVIRONMENT } from './environment.js';
import { clearSession } from '../scopes/authentication/services/api.js';
import { get } from 'svelte/store';
export const getOptions = () => ({
    environment: ENVIRONMENT,
    token: localStorage.getItem('s8:session:token') ?? '',
});
/* ERROR HANDLING ------------------------------------------------------------------------------ */
const showError = (error) => {
    addMessage({ ...error, type: 'error' });
};
/* eslint-disable max-lines-per-function, complexity */
export const handleApiError = (statusCode) => {
    switch (statusCode) {
        case 'STATUS_401':
            clearSession();
            return;
        case 'STATUS_403':
        case 'STATUS_405':
            showError({
                description: 'global.errors.unauthorized.description',
                heading: 'global.errors.unauthorized.heading',
            });
            return;
        case 'STATUS_404':
            showError({
                description: 'global.errors.resource_not_found.description',
                heading: 'global.errors.resource_not_found.heading',
            });
            return;
        case 'STATUS_409':
            showError({
                description: 'global.errors.conflict.description',
                heading: 'global.errors.conflict.heading',
            });
            return;
        case 'STATUS_410':
            showError({
                description: 'global.errors.resource_gone.description',
                heading: 'global.errors.resource_gone.heading',
            });
            return;
        case 'STATUS_412':
            showError({
                description: 'global.errors.precondition_failed.description',
                heading: 'global.errors.precondition_failed.heading',
            });
            return;
        case 'STATUS_429':
            showError({
                description: 'global.errors.too_many_requests.description',
                heading: 'global.errors.too_many_requests.heading',
            });
            return;
        case 'STATUS_503':
            showError({
                description: 'global.errors.maintenance.description',
                heading: 'global.errors.maintenance.heading',
            });
            return;
        case 'STATUS_400':
        case 'STATUS_422':
        case 'STATUS_500':
        case 'STATUS_000':
        default:
            showError({
                description: 'global.errors.general_error.description',
                heading: 'global.errors.general_error.heading',
            });
    }
};
/* FILTER -------------------------------------------------------------------------------------- */
// Casts arrays to pipe-delimited strings and booleans to integers
const formatArraysAndBooleans = ([key, value]) => {
    if (typeof value === 'boolean') {
        return [key, value ? 1 : 0];
    }
    if (Array.isArray(value)) {
        return [key, value.join('|')];
    }
    // This doesn't really feature in our APIs - just added in case
    if (typeof value === 'object') {
        return [key, JSON.stringify(value)];
    }
    return [key, value];
};
// Checks if value is not undefined, null or empty string.
const filterEmpty = ([, value]) => value !== undefined
    && value !== ''
    && value !== null;
// Cleans the given parameters by removing empty entries and casting values.
export const sanitize = (parameters = {}) => {
    const SANITIZED_PARAMETERS = Object.entries(parameters)
        .map(formatArraysAndBooleans)
        .filter(filterEmpty);
    return Object.fromEntries(SANITIZED_PARAMETERS);
};
export const handleFilter = async (request, parameters) => {
    const PARAMETERS = sanitize(parameters);
    const OPTIONS = getOptions();
    const KEY = addLoader();
    const RESPONSE = await request(PARAMETERS, OPTIONS);
    removeLoader(KEY);
    const { statusCode: STATUS_CODE, data: DATA } = RESPONSE;
    if (STATUS_CODE !== 'STATUS_200') {
        handleApiError(STATUS_CODE);
        return [];
    }
    return DATA;
};
/* FETCH --------------------------------------------------------------------------------------- */
export const handleFetch = async (request, keys) => {
    const OPTIONS = getOptions();
    const KEY = addLoader();
    const RESPONSE = await request(keys, OPTIONS);
    removeLoader(KEY);
    return { data: RESPONSE.data, statusCode: RESPONSE.statusCode };
};
/* FETCH QUERY -------------------------------------------------------------------------------------- */
// This is a non-standard version of Fetch & Filter (it accepts Query Parameters and outputs an object)
export const handleFetchQuery = async (request, parameters) => {
    const PARAMETERS = sanitize(parameters);
    const OPTIONS = getOptions();
    const KEY = addLoader();
    const RESPONSE = await request(PARAMETERS, OPTIONS);
    removeLoader(KEY);
    const { statusCode: STATUS_CODE, data: DATA } = RESPONSE;
    if (STATUS_CODE !== 'STATUS_200') {
        handleApiError(STATUS_CODE);
        return {};
    }
    return DATA;
};
export const handleUpdate = async (request, keys, body, { onError, refresh } = {}) => {
    const OPTIONS = getOptions();
    const KEY = addLoader();
    const RESPONSE = await request(keys, body, OPTIONS);
    removeLoader(KEY);
    const { statusCode: STATUS_CODE, data: DATA } = RESPONSE;
    onError?.(STATUS_CODE);
    if (STATUS_CODE !== 'STATUS_200' && STATUS_CODE !== 'STATUS_204') {
        handleApiError(STATUS_CODE);
        return DATA;
    }
    if (STATUS_CODE === 'STATUS_204') {
        refresh?.();
    }
    return DATA;
};
/* Create --------------------------------------------------------------------------------------- */
export const handleCreate = async (request, body, { onError, refresh } = {}) => {
    const OPTIONS = getOptions();
    const KEY = addLoader();
    const RESPONSE = await request(body, OPTIONS);
    removeLoader(KEY);
    const { statusCode: STATUS_CODE, data: DATA } = RESPONSE;
    onError?.(STATUS_CODE);
    if (STATUS_CODE !== 'STATUS_201' && STATUS_CODE !== 'STATUS_204') {
        handleApiError(STATUS_CODE);
        return DATA;
    }
    if (STATUS_CODE === 'STATUS_204') {
        refresh?.();
    }
    return DATA;
};
/* Delete --------------------------------------------------------------------------------------- */
export const handleDelete = async (request, keys) => {
    const OPTIONS = getOptions();
    const KEY = addLoader();
    const RESPONSE = await request(keys, OPTIONS);
    removeLoader(KEY);
    // @ts-expect-error - Fix Pegagus types
    const { statusCode: STATUS_CODE, data: DATA } = RESPONSE;
    if (STATUS_CODE !== 'STATUS_204') {
        handleApiError(STATUS_CODE);
        return DATA;
    }
    return null;
};
/* Send --------------------------------------------------------------------------------------- */
export const handleSend = async (request, keys, body) => {
    const OPTIONS = getOptions();
    const KEY = addLoader();
    const RESPONSE = await request(keys, body, OPTIONS);
    removeLoader(KEY);
    const { statusCode: STATUS_CODE, data: DATA } = RESPONSE;
    if (STATUS_CODE !== 'STATUS_204' && STATUS_CODE !== 'STATUS_200') {
        handleApiError(STATUS_CODE);
        return { data: DATA, statusCode: STATUS_CODE };
    }
    return { data: DATA, statusCode: STATUS_CODE };
};
/* Download --------------------------------------------------------------------------------------- */
export const handleDownload = async (request, keys) => {
    const OPTIONS = getOptions();
    const KEY = addLoader();
    const RESPONSE = await request(keys, OPTIONS);
    removeLoader(KEY);
    const { statusCode: STATUS_CODE, data: DATA } = RESPONSE;
    if (STATUS_CODE !== 'STATUS_200') {
        handleApiError(STATUS_CODE);
        return DATA;
    }
    return DATA;
};
/* Upload --------------------------------------------------------------------------------------- */
export const handleUpload = async (request, keys, body) => {
    const OPTIONS = getOptions();
    const KEY = addLoader();
    const RESPONSE = await request(keys, body, OPTIONS);
    removeLoader(KEY);
    const { statusCode: STATUS_CODE, data: DATA } = RESPONSE;
    if (STATUS_CODE !== 'STATUS_202') {
        handleApiError(STATUS_CODE);
        return DATA;
    }
    return null;
};
/* LINK --------------------------------------------------------------------------------------- */
export const handleLink = async (request, body) => {
    const OPTIONS = getOptions();
    const RESPONSE = await request(body, OPTIONS);
    const { statusCode: STATUS_CODE, data: DATA } = RESPONSE;
    if (STATUS_CODE !== 'STATUS_204') {
        handleApiError(STATUS_CODE);
        return DATA;
    }
    return null;
};
export const refreshHash = (HASH_STORE) => {
    if (HASH_STORE === null) {
        return;
    }
    HASH_STORE.set(crypto.randomUUID());
};
export const updateStoreBulk = (STORE, items) => {
    const DATA = get(STORE);
    items.forEach(item => {
        DATA[item._.key] = { ...DATA[item._.key], ...item };
    });
    STORE.set(DATA);
    return items;
};
export const updateStore = (STORE, HASH_STORE, response) => {
    if ('_' in response) {
        STORE.update(data => {
            data[response._.key] = response;
            return data;
        });
        if (HASH_STORE !== null) {
            HASH_STORE.set(crypto.randomUUID());
        }
    }
    return response;
};
export const removeFromStore = (STORE, HASH_STORE, key, response) => {
    if (response === null) {
        STORE.update(data => {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete data[key];
            return data;
        });
        HASH_STORE.set(crypto.randomUUID());
    }
    return response;
};
/* eslint-enable */ 
